import { useState } from "react";

import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import { shouldRedirectForAuthFailure } from "~/v2/api/auth/authFailure";
import {
  getErrorMessage,
  useGetCurrentAccountIfLiveElseTradingAccount,
  useGetIsLegalEntity,
  useGetOwnerDetails,
  useGetOwners,
  useIsCryptoSupported,
  useMutationRequest,
} from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import { patchDetails, patchEsign } from "~/v2/api/rest/account";
import { postAgreementForLegalEntityAndSigner } from "~/v2/api/rest/entityOnboarding";
import { AGREEMENTS } from "~/v2/constants";
import type { AccountAgreementCardPresentationProps } from "./AccountAgreementCard/AccountAgreementCard.pres";
import { filterAgreements } from "./AccountAgreementCard/utils";
import type { UpdateCustomerAgreementPresentationProps } from "./UpdateCustomerAgreement/UpdateCustomerAgreement.pres";

// Shared error handling for the agreement sign mutations: ignore auth-failure
// redirects, otherwise surface a toast and report to Sentry.
const handleSignError =
  (title: string) => (error: Parameters<typeof getErrorMessage>[0]) => {
    if (shouldRedirectForAuthFailure(error)) {
      return;
    }
    toastError(`${title}: ${getErrorMessage(error)}`);
    Sentry.captureException(error);
  };

export type AgreementsPresentationProps =
  | { isLoading: true }
  | {
      isLoading: false;
      isLatestAgreementRequired: boolean;
      isMissingSignerPartyId: boolean;
      customerAgreement: UpdateCustomerAgreementPresentationProps;
      agreementsCard: AccountAgreementCardPresentationProps;
    };

export const useAgreementsController = (): AgreementsPresentationProps => {
  const { account } = useGetCurrentAccountIfLiveElseTradingAccount();
  const { details } = useGetOwnerDetails();

  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();
  const { owners, isLoading: isOwnersLoading } = useGetOwners({
    accountId: account?.id ?? "",
    options: {
      enabled: !!account?.id && isLegalEntity && !isLegalEntityLoading,
    },
  });

  const accountId = account?.id ?? "";
  const legalEntityOwner = owners?.find((owner) => owner.is_legal_entity);
  const accountOpener = owners?.find((owner) => owner.is_legal_account_opener);
  const isMissingSignerPartyId =
    !isLegalEntityLoading &&
    !isOwnersLoading &&
    isLegalEntity &&
    (!legalEntityOwner || !accountOpener);

  const { isCryptoSupported } = useIsCryptoSupported();

  const queryClient = useQueryClient();
  const [openModal, setOpenModal] = useState<"crypto" | "customer" | null>(
    null,
  );

  const invalidateDetails = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.accounts.details(accountId),
    });

  // Entities sign via the legal entity + authorized signer endpoint; individuals
  // patch their own details/esign record.
  const cryptoSign = useMutationRequest(
    async () =>
      isLegalEntity
        ? postAgreementForLegalEntityAndSigner({
            ownerId: legalEntityOwner?.owner_id ?? "",
            agreement: "crypto_agreement",
            accountId,
            signerPartyId: accountOpener?.owner_id ?? "",
            revision: AGREEMENTS.crypto_agreement.revision,
          })
        : patchDetails(accountId, {
            crypto_agreement_signed_at: new Date(),
            crypto_agreement_revision: AGREEMENTS.crypto_agreement.revision,
          }),
    {
      onSuccess: () => {
        invalidateDetails();
        setOpenModal(null);
        toastSuccess("Agreement has been signed");
      },
      onError: handleSignError("Error signing crypto agreement"),
    },
  );

  const customerSign = useMutationRequest(
    async () =>
      isLegalEntity
        ? postAgreementForLegalEntityAndSigner({
            ownerId: legalEntityOwner?.owner_id ?? "",
            agreement: "customer_agreement",
            accountId,
            signerPartyId: accountOpener?.owner_id ?? "",
            revision: AGREEMENTS.customer_agreement.revision,
          })
        : patchEsign(accountId, {
            agreement: "customer_agreement",
            revision: AGREEMENTS.customer_agreement.revision,
            signed_at: new Date(),
          }),
    {
      onSuccess: () => {
        invalidateDetails();
        setOpenModal(null);
        toastSuccess("Agreement has been signed");
      },
      onError: handleSignError("Failed to update the customer agreement"),
    },
  );

  const isLatestAgreementRequired = Boolean(
    details &&
      !details.esign_audit?.find(
        ({ agreement, revision }) =>
          agreement === "customer_agreement" &&
          revision === AGREEMENTS.customer_agreement.revision,
      ),
  );

  const isSignerLoading =
    isLegalEntityLoading || (isLegalEntity && isOwnersLoading);

  if (!account || !details || isSignerLoading) {
    return { isLoading: true };
  }

  const cryptoAgreements = filterAgreements(
    details.esign_audit,
    "crypto_agreement",
  );
  const customerAgreements = filterAgreements(
    details.esign_audit,
    "customer_agreement",
  );
  const optionsAgreements = filterAgreements(
    details.esign_audit,
    "options_agreement",
  );

  const isCryptoSigned = !!details.crypto_agreement_signed_at;
  // Signed if the legacy fields say so, OR if esign_audit has ever recorded
  // a customer_agreement signature (covers accounts whose legacy signed_at
  // fields were never backfilled).
  const isCustomerSigned =
    !!details.margin_agreement_signed_at ||
    !!details.customer_agreement_signed_at ||
    customerAgreements.length > 0;
  const isOptionsSigned = !!details.options_agreement_signed_at;

  // Keep the crypto row visible when supported or if an agreement was ever signed
  const cryptoEnabled =
    isCryptoSupported || isCryptoSigned || cryptoAgreements.length > 0;

  return {
    isLoading: false,
    isLatestAgreementRequired,
    isMissingSignerPartyId,
    customerAgreement: {
      isModalOpen: openModal === "customer",
      isSubmitting: customerSign.isPending,
      isMissingSignerPartyId,
      onView: () => setOpenModal("customer"),
      onCancel: () => setOpenModal(null),
      onAgree: () => customerSign.mutate(true),
    },
    agreementsCard: {
      cryptoEnabled,
      isCryptoSigned,
      isCustomerSigned,
      isOptionsSigned,
      cryptoAgreements,
      customerAgreements,
      optionsAgreements,
      isModalOpen: openModal === "crypto",
      isSubmitting: cryptoSign.isPending,
      isMissingSignerPartyId,
      onView: () => setOpenModal("crypto"),
      onCancelModal: () => setOpenModal(null),
      onAgree: () => cryptoSign.mutate(true),
    },
  };
};
